import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import "Common/Auth/auth.css";

const EmailConfirmation = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const urlToken = queryParams.get("token");
  const userId = queryParams.get("userId");

  const handleRegister = async (values) => {
    if (!urlToken) {
      toast.error("Registration token is missing");
      return;
    }
    setLoading(true);
    const BASE_URL = process.env.REACT_APP_API_URL;
    try {
      await axios.post(`${BASE_URL}/Account/Individual/Set-Password`, {
        userId: Number(userId),
        token: urlToken,
        password: values.password,
      });
      toast.success("Your password is set. You can log in now.");
      navigate("/login");
    } catch (error) {
      const data = error.response?.data;
      const errorMessage =
        data?.message || data?.title || "Could not set your password. The link may have expired.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ciq-auth">
      <aside className="ciq-auth__brand">
        <div className="ciq-auth__brand-bg" />
        <div className="ciq-auth__brand-inner">
          <a className="ciq-brand" href="https://coreiqpro.com">
            <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="CoreIQPro" />
            <span className="ciq-brand__txt">
              Core<span className="o">IQ</span>Pro
            </span>
          </a>
          <h1 className="ciq-auth__headline">One last step.</h1>
          <p className="ciq-auth__sub">
            Set a password to activate your account and start learning with CoreIQPro.
          </p>
          <ul className="ciq-auth__points">
            <li>
              <i className="fas fa-check" /> At least 8 characters
            </li>
            <li>
              <i className="fas fa-check" /> Private to you
            </li>
            <li>
              <i className="fas fa-check" /> You are almost in
            </li>
          </ul>
        </div>
      </aside>

      <main className="ciq-auth__panel">
        <motion.div
          className="ciq-auth__card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="ciq-auth__card-head">
            <div>
              <h2>Set your password</h2>
              <p className="ciq-auth__muted">Create a password to finish setting up your account.</p>
            </div>
          </div>

          {!urlToken ? (
            <>
              <div className="ciq-auth__msg error">
                <div className="ciq-auth__msg-title">Invalid link</div>
                This activation link is missing or invalid. Please use the link from your email, or
                register again.
              </div>
              <div className="ciq-auth__foot">
                <Button type="link" onClick={() => navigate("/register")}>
                  Back to register
                </Button>
              </div>
            </>
          ) : (
            <>
              <Form name="set-password-form" layout="vertical" onFinish={handleRegister} requiredMark={false}>
                <Form.Item
                  name="password"
                  label="Password"
                  hasFeedback
                  rules={[
                    { required: true, message: "Please enter your password!" },
                    { min: 8, message: "Password must be at least 8 characters!" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your password"
                    autoComplete="new-password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm password"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Please confirm your password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
                      {loading ? "Setting up..." : "Set password"}
                    </Button>
                  </motion.div>
                </Form.Item>
              </Form>

              <div className="ciq-auth__foot">
                <span className="ciq-auth__muted">Already have an account?</span>
                <Button type="link" style={{ padding: "0 6px" }} onClick={() => navigate("/login")}>
                  Log in
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default EmailConfirmation;
